import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BRANDS, type KeapCampaignInfo } from "./types";
import { statusLabel, statusVariant } from "./flow";
import { LoadError } from "@/components/LoadError";

/** The Keap campaigns rebuilt here: install them in shadow mode to compare with Keap. */
function KeapCampaignsCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["automations-keap"],
    queryFn: () => api<{ campaigns: KeapCampaignInfo[]; not_rebuilt: Record<string, string> }>("/api/v1/automations/keap"),
  });
  const install = useMutation({
    mutationFn: () => api("/api/v1/automations/keap/install", { json: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["automations"] });
      void qc.invalidateQueries({ queryKey: ["automations-keap"] });
    },
  });
  const missing = (q.data?.campaigns ?? []).filter((c) => !c.automation_id).length;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Keap campaigns rebuilt here</CardTitle>
        {missing > 0 && (
          <Button
            size="sm"
            disabled={install.isPending}
            onClick={() => {
              if (window.confirm(`Install ${missing} rebuilt campaign(s) in shadow mode? They only log what they would do; nothing is sent.`)) install.mutate();
            }}
          >
            Install {missing} in shadow mode
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Shadow mode walks every step and logs what would happen, so each run can be compared with what Keap really sent. Keap's
          decision conditions weren't in its export: each campaign lists what was assumed.
        </p>
        {q.error && <LoadError className="px-0" what="the Keap campaigns" error={q.error} onRetry={() => q.refetch()} />}
        {(q.data?.campaigns ?? []).map((c) => (
          <details key={c.keap_campaign_id + c.name} className="rounded-md border px-3 py-2">
            <summary className="flex cursor-pointer flex-wrap items-center gap-2">
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Keap #{c.keap_campaign_id} · {BRANDS[c.brand_id]} · {c.steps} steps, {c.emails} emails
              </span>
              {c.automation_id ? (
                <Link to="/automations/$automationId" params={{ automationId: String(c.automation_id) }} className="ml-auto">
                  <Badge variant={statusVariant(c.status ?? "")}>{statusLabel(c.status ?? "")}</Badge>
                </Link>
              ) : (
                <Badge variant="muted" className="ml-auto">
                  not installed
                </Badge>
              )}
            </summary>
            {c.assumptions.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-xs">
                {c.assumptions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            )}
            {c.dropped.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs text-[var(--color-muted-foreground)]">
                {c.dropped.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            )}
          </details>
        ))}
        {q.data && Object.keys(q.data.not_rebuilt).length > 0 && (
          <details className="text-xs text-[var(--color-muted-foreground)]">
            <summary className="cursor-pointer">Not rebuilt ({Object.keys(q.data.not_rebuilt).length})</summary>
            <ul className="mt-1 list-disc pl-5">
              {Object.entries(q.data.not_rebuilt).map(([id, why]) => (
                <li key={id}>
                  #{id}: {why}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

/** Page: the Keap campaigns rebuilt as automations (route /automations/keap). */
export function KeapCampaignsPage() {
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <div className="text-xs text-[var(--color-muted-foreground)]">
        <Link to="/automations" className="hover:underline">
          Automations
        </Link>
      </div>
      <KeapCampaignsCard />
    </div>
  );
}
